import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  CheckSquare, 
  Plus, 
  Bell,
  Sparkles,
  Clock,
  User,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Circle,
  Users,
  RefreshCw,
  ListTodo,
  Sun,
  Moon,
  Settings,
  LogOut,
  MessageCircle,
  ArrowUpRight,
  ArrowDownLeft,
  Handshake,
  ExternalLink,
  Loader2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import CreateTaskModal from '../components/CreateTaskModal';
import GroupTaskModal from '../components/GroupTaskModal';
import UserSettings from '../components/UserSettings';
import TaskFilter from '../components/TaskFilter';
import BulkActions from '../components/BulkActions';
import TeamMembersAvatars from '../components/TeamMembersAvatars';
import DirectMessagePanel from '../components/DirectMessagePanel';
import PinnedTaskBundles from '../components/PinnedTaskBundles';
import { Checkbox } from '../components/ui/checkbox';
import { formatDistanceToNow, parseISO, isPast, isToday, compareAsc } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collaborator Card Component for carousel
const CollaboratorCard = ({ collab, onMessage, onTaskClick, isMobile }) => (
  <Card 
    className={`
      ${isMobile 
        ? 'flex-shrink-0 w-[240px]' 
        : 'flex-shrink-0 w-[280px]'
      } 
      cursor-pointer hover:shadow-md transition-all hover:scale-[1.01] group
    `}
    data-testid={`collaborator-card-${collab.id}`}
    onClick={onTaskClick}
  >
    <CardContent className={isMobile ? "p-3" : "p-4"}>
      <div className="flex flex-col gap-3">
        {/* Avatar and Online Status */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-11 w-11 border-2 border-border dark:border-zinc-700">
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground text-sm font-medium">
                {collab.name?.charAt(0).toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            {collab.is_online && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-card" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{collab.name}</p>
            <p className="text-xs text-muted-foreground truncate">{collab.email}</p>
          </div>
        </div>
        
        {/* Interaction Info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
          <div className="flex items-center gap-1">
            {collab.interaction_type === 'assigned_to_me' && (
              <>
                <ArrowDownLeft className="w-3 h-3 text-blue-500" />
                <span>Assigned me</span>
              </>
            )}
            {collab.interaction_type === 'i_assigned' && (
              <>
                <ArrowUpRight className="w-3 h-3 text-amber-500" />
                <span>I assigned</span>
              </>
            )}
            {collab.interaction_type === 'collaborating' && (
              <>
                <Handshake className="w-3 h-3 text-green-500" />
                <span>Collab</span>
              </>
            )}
          </div>
          {/* Task count - clickable to show tasks modal */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTaskClick();
            }}
            className="cursor-pointer"
            data-testid={`task-count-${collab.id}`}
          >
            <Badge 
              variant="outline" 
              className="text-[10px] px-1.5 py-0.5 hover:bg-primary/10 transition-colors"
            >
              <ListTodo className="w-3 h-3 mr-1" />
              {collab.active_tasks_together} task{collab.active_tasks_together !== 1 ? 's' : ''}
            </Badge>
          </button>
        </div>
        
        {/* Message Button */}
        <Button 
          variant="secondary" 
          size="sm" 
          className="w-full h-8 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onMessage();
          }}
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
          Message
          {collab.unread_dm_count > 0 && (
            <span className="ml-1.5 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
              {collab.unread_dm_count > 9 ? '9+' : collab.unread_dm_count}
            </span>
          )}
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Subtask Progress Component with expandable list - Compact design
const SubtaskProgress = ({ task, expanded, subtasks, loading, onToggle }) => {
  if (!task.has_subtasks) return null;
  
  const count = task.subtask_count || 0;
  const completed = task.subtask_completed || 0;
  const percentage = count > 0 ? Math.round((completed / count) * 100) : 0;
  
  // Progress bar color based on completion
  const getProgressColor = () => {
    if (percentage >= 70) return 'bg-green-500';
    if (percentage >= 30) return 'bg-amber-500';
    return 'bg-orange-500';
  };
  
  return (
    <div className="mt-3 pt-3 border-t border-border/50">
      {/* Compact Progress Bar Header - Single Line */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {/* Left side: Label + Progress Bar + Counter */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <ListTodo className="w-3.5 h-3.5" />
            <span className="font-medium">Sub-tasks</span>
          </div>
          
          {/* Compact Progress Bar - Fixed 80px width */}
          <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full ${getProgressColor()} rounded-full transition-all duration-300`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          
          {/* Counter */}
          <span className="font-medium text-foreground">
            {completed}/{count} ({percentage}%)
          </span>
        </div>
        
        {/* Right side: Chevron toggle */}
        <div className="flex items-center">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : expanded ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </div>
      </button>
      
      {/* Expandable Subtask List */}
      {expanded && subtasks && (
        <div className="mt-3 space-y-1.5 max-h-[200px] overflow-y-auto">
          {subtasks.slice(0, 5).map((subtask) => (
            <div 
              key={subtask.id}
              className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-accent/30 transition-colors"
            >
              {subtask.status === 'completed' ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
              ) : subtask.status === 'in progress' ? (
                <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              )}
              <span className={`flex-1 truncate ${subtask.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                {subtask.name}
              </span>
              {subtask.status === 'in progress' && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-amber-500/10 text-amber-600 border-amber-500/30">
                  Active
                </Badge>
              )}
            </div>
          ))}
          {subtasks.length > 5 && (
            <p className="text-[10px] text-muted-foreground text-center pt-1">
              +{subtasks.length - 5} more sub-tasks
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const DashboardNew = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [secondaryTasks, setSecondaryTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGroupTaskModal, setShowGroupTaskModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    assignee: 'all',
    dateFrom: null,
    dateTo: null
  });
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [dmMember, setDmMember] = useState(null); // For direct message panel
  const [dmRefreshTrigger, setDmRefreshTrigger] = useState(0); // To trigger DM count refresh
  const [collaborators, setCollaborators] = useState([]); // People I'm Working With
  const [carouselIndex, setCarouselIndex] = useState(0); // Carousel current index
  const [sharedTasksModal, setSharedTasksModal] = useState({ open: false, collaborator: null, tasks: [], loading: false });
  const [expandedSubtasks, setExpandedSubtasks] = useState({}); // Track expanded task subtasks { taskId: subtasks[] }
  const [loadingSubtasks, setLoadingSubtasks] = useState({}); // Track loading state for subtasks

  const MAX_DISPLAY_TASKS = 20;
  
  // Responsive cards per view - will be calculated based on container width
  const CARD_WIDTH = 280; // Fixed card width
  const CARD_GAP = 16; // Gap between cards
  const getCardsPerView = () => {
    if (typeof window === 'undefined') return 3;
    const width = window.innerWidth;
    if (width >= 1600) return 4; // Wide monitors
    if (width >= 1200) return 3; // Standard desktop
    if (width >= 900) return 2;  // Tablet
    return 1;
  };
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  
  // Update cards per view on resize
  useEffect(() => {
    const handleResize = () => {
      const newCardsPerView = getCardsPerView();
      setCardsPerView(newCardsPerView);
      // Reset carousel index if it would be out of bounds
      const maxIndex = Math.max(0, collaborators.length - newCardsPerView);
      if (carouselIndex > maxIndex) {
        setCarouselIndex(maxIndex);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collaborators.length, carouselIndex]);

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.assignee && filters.assignee !== 'all') params.append('assignee', filters.assignee);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('date_to', filters.dateTo.toISOString());

      const queryString = params.toString();
      const url = `${API}/tasks${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [token, filters]);

  const fetchSecondaryTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/secondary-participating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSecondaryTasks(data);
      }
    } catch (error) {
      console.error('Error fetching secondary tasks:', error);
    }
  }, [token]);

  const fetchCompletedTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedTasks(data);
      }
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
    }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchCollaborators = useCallback(async () => {
    try {
      const response = await fetch(`${API}/collaborators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollaborators(data.collaborators || []);
        setCarouselIndex(0); // Reset carousel when data changes
      }
    } catch (error) {
      console.error('Error fetching collaborators:', error);
    }
  }, [token]);

  const fetchSharedTasks = async (collaborator) => {
    setSharedTasksModal({ open: true, collaborator, tasks: [], loading: true });
    try {
      const response = await fetch(`${API}/collaborators/${collaborator.id}/shared-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSharedTasksModal({ open: true, collaborator, tasks: data.shared_tasks || [], loading: false });
      } else {
        setSharedTasksModal({ open: true, collaborator, tasks: [], loading: false });
        toast.error('Failed to load shared tasks');
      }
    } catch (error) {
      console.error('Error fetching shared tasks:', error);
      setSharedTasksModal({ open: true, collaborator, tasks: [], loading: false });
      toast.error('Failed to load shared tasks');
    }
  };

  // Carousel navigation handlers
  const handleCarouselPrev = () => {
    setCarouselIndex(prev => Math.max(0, prev - 1));
  };

  const handleCarouselNext = () => {
    const maxIndex = Math.max(0, collaborators.length - cardsPerView);
    setCarouselIndex(prev => Math.min(maxIndex, prev + 1));
  };

  // Calculate carousel metrics
  const maxCarouselIndex = Math.max(0, collaborators.length - cardsPerView);
  const canScrollPrev = carouselIndex > 0;
  const canScrollNext = carouselIndex < maxCarouselIndex;
  const totalPages = Math.ceil(collaborators.length / cardsPerView);
  const currentPage = Math.floor(carouselIndex / cardsPerView) + 1;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTasks(),
        fetchSecondaryTasks(),
        fetchCompletedTasks(),
        fetchNotifications(),
        fetchUsers(),
        fetchCollaborators()
      ]);
      setLoading(false);
      setInitialLoadComplete(true);
    };
    loadData();

    const notificationInterval = setInterval(fetchNotifications, 30000);
    const taskPollInterval = setInterval(fetchTasks, 30000);
    const collaboratorPollInterval = setInterval(fetchCollaborators, 60000); // Poll collaborators every minute

    return () => {
      clearInterval(notificationInterval);
      clearInterval(taskPollInterval);
      clearInterval(collaboratorPollInterval);
    };
  }, [token]);

  // Toggle subtasks expansion for a task
  const toggleSubtasks = async (taskId, e) => {
    e.stopPropagation();
    
    // If already expanded, collapse
    if (expandedSubtasks[taskId]) {
      setExpandedSubtasks(prev => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
      return;
    }
    
    // Fetch subtasks
    setLoadingSubtasks(prev => ({ ...prev, [taskId]: true }));
    try {
      const response = await fetch(`${API}/tasks/${taskId}/subtasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const subtasks = await response.json();
        setExpandedSubtasks(prev => ({ ...prev, [taskId]: subtasks }));
      }
    } catch (error) {
      console.error('Error fetching subtasks:', error);
    } finally {
      setLoadingSubtasks(prev => ({ ...prev, [taskId]: false }));
    }
  };

  useEffect(() => {
    if (initialLoadComplete) {
      fetchTasks();
    }
  }, [filters, initialLoadComplete, fetchTasks]);

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleRefreshTasks = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchTasks(), fetchSecondaryTasks()]);
    setIsRefreshing(false);
    toast.success('Tasks refreshed!');
  };

  const handleTaskCreated = () => {
    fetchTasks();
    fetchSecondaryTasks();
    setShowCreateModal(false);
    toast.success('Task created successfully!');
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
      acknowledged: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
      'in progress': 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30',
      completed: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30'
    };
    return styles[status] || '';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30',
      medium: 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30',
      low: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30'
    };
    return styles[priority] || '';
  };

  const getDueDateInfo = (dueDate) => {
    const date = parseISO(dueDate);
    const isOverdue = isPast(date) && !isToday(date);
    const isDueToday = isToday(date);
    return {
      text: formatDistanceToNow(date, { addSuffix: true }),
      isOverdue,
      isDueToday
    };
  };

  const sortTasks = (taskList) => {
    const statusOrder = { 'new': 0, 'in progress': 1, 'completed': 2 };
    return [...taskList].sort((a, b) => {
      const dateA = parseISO(a.due_date);
      const dateB = parseISO(b.due_date);
      const dateCompare = compareAsc(dateA, dateB);
      if (dateCompare !== 0) return dateCompare;
      const statusA = statusOrder[a.status] ?? 3;
      const statusB = statusOrder[b.status] ?? 3;
      return statusA - statusB;
    });
  };

  const myTasks = sortTasks(
    tasks.filter(t => t.assigned_to === user?.id && t.status !== 'completed')
  );
  const assignedByMe = tasks.filter(t => t.assigned_by === user?.id && t.assigned_to !== user?.id && t.status !== 'completed');
  const mySecondaryTasks = sortTasks(
    secondaryTasks.filter(t => t.status !== 'completed')
  );

  const highPriorityTasks = myTasks.filter(t => t.ai_priority === 'high');
  const inProgressTasks = tasks.filter(t =>
    (t.assigned_to === user?.id || t.assigned_by === user?.id) &&
    t.status === 'in progress'
  );

  // Task selection handlers
  const handleTaskSelect = (taskId, e) => {
    e.stopPropagation();
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        const newSelection = prev.filter(id => id !== taskId);
        if (newSelection.length === 0) setSelectionMode(false);
        return newSelection;
      } else {
        if (!selectionMode) setSelectionMode(true);
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = (taskList) => {
    const taskIds = taskList.map(t => t.id);
    const allSelected = taskIds.every(id => selectedTasks.includes(id));
    if (allSelected) {
      setSelectedTasks(prev => prev.filter(id => !taskIds.includes(id)));
    } else {
      setSelectedTasks(prev => [...new Set([...prev, ...taskIds])]);
    }
    setSelectionMode(true);
  };

  const clearSelection = () => {
    setSelectedTasks([]);
    setSelectionMode(false);
  };

  const handleBulkActionComplete = () => {
    fetchTasks();
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar hideHeaderControls>
      <div className="min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          {/* Row 1: Title, Members, Notifications, Theme, Profile */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="pl-10 lg:pl-0">
              <h1 className="font-heading text-xl sm:text-2xl font-bold">Dashboard</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Welcome back, {user?.name?.split(' ')[0]}</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Team Members Avatars */}
              <TeamMembersAvatars 
                onOpenChat={(member) => setDmMember(member)} 
                refreshTrigger={dmRefreshTrigger}
              />

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="font-medium">Notifications</span>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <DropdownMenuItem
                          key={notif.id}
                          className={`p-3 cursor-pointer ${!notif.read ? 'bg-primary/5' : ''}`}
                          onClick={() => notif.task_id && navigate(`/tasks/${notif.task_id}`)}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDistanceToNow(parseISO(notif.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="header-theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* User Profile Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="header-user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="header-menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-500" data-testid="header-menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Row 2: Action Buttons (New Task, Assign to Group) - aligned right */}
          <div className="px-4 sm:px-6 pb-3 flex justify-end gap-2">
            <Button onClick={() => setShowCreateModal(true)} data-testid="new-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
            <Button variant="outline" onClick={() => setShowGroupTaskModal(true)} data-testid="assign-group-btn">
              <Users className="w-4 h-4 mr-2" />
              Assign to Group
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-x-hidden">
          {/* Search & Filter */}
          <TaskFilter onFilterChange={handleFilterChange} initialFilters={filters} />

          {/* Stats Cards - Horizontally scrollable on mobile */}
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 sm:gap-4 sm:overflow-visible scrollbar-hide">
            <Card className="cursor-pointer hover:shadow-md transition-shadow flex-shrink-0 w-[140px] sm:w-auto" onClick={() => navigate('/history')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
                    <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold">{myTasks.length}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">My Tasks</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow flex-shrink-0 w-[140px] sm:w-auto">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-red-500/10">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold">{highPriorityTasks.length}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">High Priority</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow flex-shrink-0 w-[140px] sm:w-auto">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-amber-500/10">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold">{inProgressTasks.length}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow flex-shrink-0 w-[140px] sm:w-auto" onClick={() => navigate('/assigned')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-blue-500/10">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold">{assignedByMe.length}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Assigned</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow flex-shrink-0 w-[140px] sm:w-auto" onClick={() => navigate('/completed')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-500/10">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold">{completedTasks.length}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* People I'm Working With Section - Carousel */}
          {collaborators.length > 0 && (
            <div className="space-y-3" data-testid="people-working-with-section">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg sm:text-xl font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  People I'm Working With
                  <Badge variant="secondary" className="ml-1">{collaborators.length}</Badge>
                </h2>
              </div>
              
              {/* Carousel Container with Side Arrows */}
              <div className="relative">
                {/* Mobile: Carousel with smaller arrows */}
                <div className="sm:hidden flex items-center justify-center gap-2">
                  {/* Left Arrow - Mobile */}
                  <button
                    onClick={handleCarouselPrev}
                    disabled={!canScrollPrev}
                    className={`
                      flex-shrink-0 w-9 h-9 rounded-full 
                      flex items-center justify-center
                      border transition-all duration-200
                      ${canScrollPrev 
                        ? 'bg-background border-border shadow-sm active:scale-95' 
                        : 'bg-muted/20 border-transparent text-muted-foreground/30'
                      }
                    `}
                    data-testid="carousel-prev-mobile"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  {/* Mobile Cards Container - Show 1 card */}
                  <div className="flex-1 overflow-hidden max-w-[240px]">
                    <div 
                      className="flex gap-3 transition-transform duration-300 ease-out"
                      style={{ 
                        transform: `translateX(-${carouselIndex * 252}px)` // 240px card + 12px gap
                      }}
                    >
                      {collaborators.map((collab) => (
                        <CollaboratorCard 
                          key={collab.id} 
                          collab={collab} 
                          onMessage={() => setDmMember(collab)}
                          onTaskClick={() => fetchSharedTasks(collab)}
                          isMobile={true}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Right Arrow - Mobile */}
                  <button
                    onClick={handleCarouselNext}
                    disabled={!canScrollNext}
                    className={`
                      flex-shrink-0 w-9 h-9 rounded-full 
                      flex items-center justify-center
                      border transition-all duration-200
                      ${canScrollNext 
                        ? 'bg-background border-border shadow-sm active:scale-95' 
                        : 'bg-muted/20 border-transparent text-muted-foreground/30'
                      }
                    `}
                    data-testid="carousel-next-mobile"
                    aria-label="Next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Desktop: Carousel with Side Navigation Arrows - Centered */}
                <div className="hidden sm:flex items-center justify-center gap-3">
                  {/* Left Arrow - Outside Carousel */}
                  <button
                    onClick={handleCarouselPrev}
                    disabled={!canScrollPrev}
                    className={`
                      flex-shrink-0 w-12 h-12 rounded-full 
                      flex items-center justify-center
                      border-2 transition-all duration-200
                      ${canScrollPrev 
                        ? 'bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-110 border-border cursor-pointer shadow-md hover:shadow-lg' 
                        : 'bg-muted/30 border-transparent text-muted-foreground/30 cursor-not-allowed'
                      }
                    `}
                    data-testid="carousel-prev"
                    aria-label="Previous collaborators"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  {/* Cards Container with Peek */}
                  <div 
                    className="flex-1 overflow-hidden relative max-w-full"
                  >
                    {/* Fade overlay on right edge to show more cards exist */}
                    {canScrollNext && (
                      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
                    )}
                    
                    <div 
                      className="flex gap-4 transition-transform duration-300 ease-out"
                      style={{ 
                        transform: `translateX(-${carouselIndex * (CARD_WIDTH + CARD_GAP)}px)`
                      }}
                    >
                      {collaborators.map((collab) => (
                        <CollaboratorCard 
                          key={collab.id} 
                          collab={collab} 
                          onMessage={() => setDmMember(collab)}
                          onTaskClick={() => fetchSharedTasks(collab)}
                          isMobile={false}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Right Arrow - Outside Carousel */}
                  <button
                    onClick={handleCarouselNext}
                    disabled={!canScrollNext}
                    className={`
                      flex-shrink-0 w-12 h-12 rounded-full 
                      flex items-center justify-center
                      border-2 transition-all duration-200
                      ${canScrollNext 
                        ? 'bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-110 border-border cursor-pointer shadow-md hover:shadow-lg' 
                        : 'bg-muted/30 border-transparent text-muted-foreground/30 cursor-not-allowed'
                      }
                    `}
                    data-testid="carousel-next"
                    aria-label="Next collaborators"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Dot Indicators - Desktop */}
                {collaborators.length > cardsPerView && (
                  <div className="hidden sm:flex justify-center gap-2 pt-3">
                    {Array.from({ length: Math.ceil(collaborators.length / cardsPerView) }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCarouselIndex(idx * cardsPerView)}
                        className={`
                          w-2 h-2 rounded-full transition-all duration-200
                          ${Math.floor(carouselIndex / cardsPerView) === idx 
                            ? 'bg-primary w-6' 
                            : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                          }
                        `}
                        aria-label={`Go to page ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Mobile Dot Indicators */}
              {collaborators.length > 1 && (
                <div className="sm:hidden flex justify-center gap-1.5 pt-2">
                  {collaborators.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCarouselIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${
                        carouselIndex === idx 
                          ? 'bg-primary w-4' 
                          : 'bg-muted-foreground/30'
                      }`}
                      aria-label={`Go to card ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pinned Task Bundles - Dashboard Module */}
          <PinnedTaskBundles />

          {/* Task Refresh Header */}
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg sm:text-xl font-semibold">Task Overview</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshTasks}
              disabled={isRefreshing}
              className="text-muted-foreground hover:text-foreground"
              data-testid="refresh-tasks-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Tasks I'm Collaborating On */}
          {mySecondaryTasks.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Tasks I'm Collaborating On
                  <Badge variant="secondary" className="ml-2">{mySecondaryTasks.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[280px] pr-4">
                  <div className="space-y-3">
                    {mySecondaryTasks.slice(0, 10).map((task) => {
                      const dueInfo = getDueDateInfo(task.due_date);
                      return (
                        <div
                          key={task.id}
                          className="p-3 rounded-lg border bg-card/50 hover:bg-accent/30 transition-colors"
                          data-testid={`secondary-task-${task.id}`}
                        >
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => navigate(`/tasks/${task.id}`)}
                          >
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm truncate">{task.name}</h4>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <span className={dueInfo.isOverdue ? 'text-red-500' : ''}>
                                  {dueInfo.text}
                                </span>
                                <span>•</span>
                                <span>{task.assigned_to_name}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`${getStatusBadge(task.status)} text-xs`}>
                                {task.status}
                              </Badge>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                          
                          {/* Subtask Progress */}
                          <SubtaskProgress 
                            task={task}
                            expanded={!!expandedSubtasks[task.id]}
                            subtasks={expandedSubtasks[task.id]}
                            loading={loadingSubtasks[task.id]}
                            onToggle={(e) => toggleSubtasks(task.id, e)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* My Tasks */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">
                  My Tasks
                  {myTasks.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{myTasks.length}</Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {myTasks.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectAll(myTasks.slice(0, MAX_DISPLAY_TASKS))}
                      className="text-xs"
                      data-testid="select-all-my-tasks"
                    >
                      {myTasks.slice(0, MAX_DISPLAY_TASKS).every(t => selectedTasks.includes(t.id))
                        ? 'Deselect All'
                        : 'Select All'}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate('/history')} data-testid="view-all-my-tasks">
                    View all
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                {myTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <CheckSquare className="w-12 h-12 mb-4 opacity-20" />
                    <p>No active tasks assigned to you</p>
                    <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Task
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myTasks.slice(0, MAX_DISPLAY_TASKS).map((task) => {
                      const dueInfo = getDueDateInfo(task.due_date);
                      const isSelected = selectedTasks.includes(task.id);
                      return (
                        <div
                          key={task.id}
                          className={`p-4 rounded-xl border bg-card transition-all ${
                            task.ai_priority === 'high' ? 'border-red-500/30 bg-red-500/5' : ''
                          } ${isSelected ? 'ring-2 ring-primary' : 'hover:bg-accent/30'}`}
                          data-testid={`task-card-${task.id}`}
                        >
                          <div 
                            className="flex items-start gap-3 cursor-pointer"
                            onClick={() => !selectionMode && navigate(`/tasks/${task.id}`)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleTaskSelect(task.id, { stopPropagation: () => {} })}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`task-checkbox-${task.id}`}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-medium truncate">{task.name}</h3>
                                {task.ai_priority && (
                                  <Badge variant="outline" className={`${getPriorityBadge(task.ai_priority)} text-xs`}>
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    {task.ai_priority}
                                  </Badge>
                                )}
                                {task.external_submitter && (
                                  <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Intake
                                  </Badge>
                                )}
                              </div>
                              {task.external_submitter && (
                                <p className="text-xs text-blue-400 mb-1">
                                  Submitted by: {task.external_submitter.email}
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {task.description || 'No description'}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className={`flex items-center gap-1 ${
                                  dueInfo.isOverdue ? 'text-red-500' : dueInfo.isDueToday ? 'text-amber-500' : ''
                                }`}>
                                  <Clock className="w-3 h-3" />
                                  {dueInfo.text}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_by_name}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge variant="outline" className={getStatusBadge(task.status)}>
                                {task.status}
                              </Badge>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                          
                          {/* Subtask Progress with expandable list */}
                          <SubtaskProgress 
                            task={task}
                            expanded={!!expandedSubtasks[task.id]}
                            subtasks={expandedSubtasks[task.id]}
                            loading={loadingSubtasks[task.id]}
                            onToggle={(e) => toggleSubtasks(task.id, e)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </main>

        {/* Create Task Modal */}
        <CreateTaskModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTaskCreated={handleTaskCreated}
        />

        {/* Group Task Modal */}
        <GroupTaskModal
          open={showGroupTaskModal}
          onClose={() => setShowGroupTaskModal(false)}
          onTaskCreated={handleTaskCreated}
        />

        {/* User Settings Modal */}
        <UserSettings
          open={showSettings}
          onClose={() => setShowSettings(false)}
        />

        {/* Bulk Actions Bar */}
        <BulkActions
          selectedTasks={selectedTasks}
          onClearSelection={clearSelection}
          onActionComplete={handleBulkActionComplete}
          token={token}
          users={allUsers}
        />

        {/* Direct Message Panel */}
        {dmMember && (
          <DirectMessagePanel
            member={dmMember}
            onClose={() => setDmMember(null)}
            onMessagesRead={() => setDmRefreshTrigger(prev => prev + 1)}
          />
        )}

        {/* Shared Tasks Modal */}
        <Dialog 
          open={sharedTasksModal.open} 
          onOpenChange={(open) => !open && setSharedTasksModal({ open: false, collaborator: null, tasks: [], loading: false })}
        >
          <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-primary" />
                Tasks with {sharedTasksModal.collaborator?.name}
              </DialogTitle>
            </DialogHeader>
            
            {sharedTasksModal.loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : sharedTasksModal.tasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <CheckSquare className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm">No active tasks together</p>
              </div>
            ) : (
              <ScrollArea className="flex-1 -mx-6 px-6">
                <div className="space-y-2 pb-4">
                  {sharedTasksModal.tasks.map((task) => (
                    <div
                      key={task.id}
                      className="p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSharedTasksModal({ open: false, collaborator: null, tasks: [], loading: false });
                        navigate(`/tasks/${task.id}`);
                      }}
                      data-testid={`shared-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-sm truncate">{task.name}</h4>
                            {task.your_role === 'owner' && (
                              <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                                Owner
                              </Badge>
                            )}
                            {task.your_role === 'assignee' && (
                              <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/30">
                                Assignee
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            <span className={`flex items-center gap-1 ${
                              task.status === 'in progress' ? 'text-amber-500' : 
                              task.status === 'new' ? 'text-purple-500' : ''
                            }`}>
                              {task.status === 'in progress' && <Clock className="w-3 h-3" />}
                              {task.status === 'new' && <AlertCircle className="w-3 h-3" />}
                              {task.status}
                            </span>
                            
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDistanceToNow(parseISO(task.due_date), { addSuffix: true })}
                              </span>
                            )}
                          </div>
                          
                          {/* Sub-task progress */}
                          {task.has_subtasks && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground">Sub-tasks</span>
                                <span className="font-medium">
                                  {task.subtask_completed}/{task.subtask_total}
                                </span>
                              </div>
                              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full transition-all"
                                  style={{ width: `${task.subtask_total > 0 ? (task.subtask_completed / task.subtask_total) * 100 : 0}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
};

export default DashboardNew;
